import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Hammer, MoveDownIcon } from "lucide-react";
import CustomInputComponent from "@/components/ui/custom-input/CustomInputComponent.tsx";
import MessageWithDbComponent from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/chat-screen/message-with-db/MessageWithDbComponent.tsx";
import UserAvatarComponent from "@/components/common-components/user-avatar/UserAvatarComponent.tsx";
import JoyAvatar from "@/components/common-components/joy-avatar/joy-avatar.tsx";
import { usePmChatState } from '@/hooks/usePmChatState';
import { usePmAgentApi } from '@/hooks/usePmAgentApi';
import { usePmSocket } from "@/hooks/usePmSocket";
import { AppType } from "@/models/project.model.ts";
import './PmAgentDialogComponent.scss';
import { setPmConversationSuccessful } from '@/store/actions/projectdata.action';
import { useDispatch } from 'react-redux';

interface PmAgentDialogProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: string;
    appType: AppType;
    onExecutePlan?: () => void;
}

const PmAgentDialogComponent = ({ isOpen, onClose, projectId, onExecutePlan }: PmAgentDialogProps) => {
    const [chatInput, setChatInput] = useState<string>('');
    const chatHistoryRef = useRef<HTMLDivElement>(null);
    const messageRefs = useRef<HTMLDivElement[]>([]);
    const [lastMessageHeight, setLastMessageHeight] = useState(0);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const dispatch = useDispatch();

    const {
        messages,
        isChatInProgress,
        setIsChatInProgress,
        addUserMessage,
        addLoadingMessage,
        updateLoadingMessage,
        handleErrorMessage,
        setInitialMessages,
        clearMessages,

    } = usePmChatState();

    // Use PM agent specific socket and API - independent conversation
    usePmSocket(updateLoadingMessage, projectId, handleErrorMessage);

    const {
        sendPmAgentMessage,
        createPmSession,
        loadPmConversationHistory,
        pmConversationHistory,
        isLoadingHistory
    } = usePmAgentApi(
        projectId,
        () => setIsChatInProgress(false),
        handleErrorMessage
    );

    const scrollToBottom = useCallback(() => {
        const scroll = () => {
            if (chatHistoryRef.current) {
                chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
            }
        };
        setTimeout(scroll, 100);
    }, []);

    const calculateLastMessageHeight = useCallback(() => {
        if (!chatHistoryRef.current || !messageRefs.current.length) return;

        setTimeout(() => {
            const chatHistoryHeight = chatHistoryRef.current?.clientHeight || 0;
            let previousUserMessageHeight = 0;

            for (let i = messageRefs.current.length - 1; i >= 0; i--) {
                const message = messageRefs.current[i];
                if (message && message.classList.contains('user')) {
                    previousUserMessageHeight = message.offsetHeight;
                    break;
                }
            }
            if (previousUserMessageHeight < chatHistoryHeight) {
                setLastMessageHeight(chatHistoryHeight - previousUserMessageHeight - 30);
            }
            else {
                setLastMessageHeight(0)
            }
            scrollToBottom();

        }, 100);
    }, [scrollToBottom]);

    const handleSendMessage = useCallback(async (input: string) => {
        if (!input.trim() && !imageFile) return;
        setChatInput("");
        setImageFile(null);
        setIsChatInProgress(true);
        addUserMessage(input);
        addLoadingMessage();
        calculateLastMessageHeight();

        // Send message with PM agent context
        await sendPmAgentMessage(input); // chatMode = true for PM agent

    }, [addUserMessage, addLoadingMessage, sendPmAgentMessage, setIsChatInProgress, calculateLastMessageHeight, imageFile]);

    const checkScrollPosition = useCallback(() => {
        if (!chatHistoryRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatHistoryRef.current;
        setShowScrollIndicator(scrollHeight - scrollTop - clientHeight > 5);

    }, []);

    useEffect(() => {
        const chatHistory = chatHistoryRef.current;
        if (chatHistory) {
            chatHistory.addEventListener('scroll', checkScrollPosition);
            return () => chatHistory.removeEventListener('scroll', checkScrollPosition);
        }
    }, [checkScrollPosition]);

    useEffect(() => {
        checkScrollPosition();
    }, [messages, checkScrollPosition]);

    const handleScrollToBottom = useCallback(() => {
        if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTo({
                top: chatHistoryRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, []);

    // Removed unused handleSessionSelect function

    const formatSessionDate = useCallback((dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const sessionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (sessionDate.getTime() === today.getTime()) {
            return '';
        } else if (sessionDate.getTime() === yesterday.getTime()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        }
    }, []);

    const formatSessionTime = useCallback((dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const sessionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (sessionDate.getTime() === today.getTime()) {
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        }
    }, []);

    // Removed unused groupedSessionsByDate function

    // Initialize with PM agent conversation history and create session if needed
    useEffect(() => {
        if (isOpen) {
            const initializePmAgent = async () => {
                try {
                    // First, try to load existing conversation history
                    await loadPmConversationHistory();

                } catch (error) {
                    console.error('Error initializing PM agent:', error);
                    // createPmSession();
                }
            };

            initializePmAgent();
        }
    }, [isOpen, setInitialMessages, createPmSession, loadPmConversationHistory]);

    // Scroll to bottom when history loading is complete
    useEffect(() => {
        if (!isLoadingHistory && pmConversationHistory?.length > 0) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                scrollToBottom();
            }, 200);
        }
    }, [isLoadingHistory, pmConversationHistory, scrollToBottom]);


    // Reset chat input and messages when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setChatInput('');
            setImageFile(null);
            clearMessages();
        }
    }, [isOpen, clearMessages]);

    const handleExecutePlan = useCallback(() => {
        if (onExecutePlan) {
            onExecutePlan();
        } else {
            // Fallback: set PM conversation as successful and close
            dispatch(setPmConversationSuccessful(true));
            onClose();
        }
    }, [onExecutePlan, dispatch, onClose]);


    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="pm-agent-dialog-content">
                <DialogHeader className="pm-agent-dialog-header">
                    <div className="pm-agent-header-content">
                        <JoyAvatar size="medium" />
                        <div className="pm-agent-info">
                            <DialogTitle className="pm-agent-title">Zoya - Product Manager</DialogTitle>
                            <p className="pm-agent-subtitle">Your AI Product Management Assistant</p>
                        </div>
                    </div>
                </DialogHeader>
                <div className="pm-agent-chat-container">
                    <div className="pm-agent-chat-history" ref={chatHistoryRef}>
                        {isLoadingHistory ? (
                            <div className="pm-agent-history-loading">
                                <div className="pm-agent-loading-skeleton">
                                    
                                    <div className="pm-agent-skeleton-messages">
                                        <div className="pm-agent-skeleton-message">
                                            <div className="pm-agent-skeleton-avatar"></div>
                                            <div className="pm-agent-skeleton-content">
                                                <div className="pm-agent-skeleton-line long"></div>
                                                <div className="pm-agent-skeleton-line medium"></div>
                                            </div>
                                        </div>
                                        <div className="pm-agent-skeleton-message user">
                                            <div className="pm-agent-skeleton-avatar"></div>
                                            <div className="pm-agent-skeleton-content">
                                                <div className="pm-agent-skeleton-line short"></div>
                                            </div>
                                        </div>
                                        <div className="pm-agent-skeleton-message">
                                            <div className="pm-agent-skeleton-avatar"></div>
                                            <div className="pm-agent-skeleton-content">
                                                <div className="pm-agent-skeleton-line long"></div>
                                                <div className="pm-agent-skeleton-line short"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="pm-agent-loading-text">Loading conversation history...</div>
                            </div>
                        ) : (
                            <>
                                {
                                    pmConversationHistory?.length > 0 &&
                                    //reverse the array
                                    pmConversationHistory?.map((session: any) => {
                                        if (session.chats?.length === 0) {
                                            return <></>;
                                        }
                                        else {
                                            return (
                                                <div className='pm-agent-session-container' key={session.id}>
                                                    <div className="pm-agent-session-header">
                                                        <span className="pm-agent-session-date">{formatSessionDate(session.created_at)}</span>
                                                        <span className="pm-agent-session-time">{formatSessionTime(session.created_at)}</span>
                                                    </div>
                                                    {session?.chats?.reverse().map((message: any, index: number) => (
                                                        <div
                                                            ref={(el: HTMLDivElement | null) => { messageRefs.current[index] = el as HTMLDivElement; }}
                                                            key={message.id}
                                                            className={`pm-agent-message ${message.role}`}
                                                            style={{
                                                                whiteSpace: 'pre-wrap',
                                                            }}
                                                        >
                                                            {(message.role === 'agent' || message.role === 'loading')
                                                                ? <JoyAvatar size="chat" />
                                                                : <UserAvatarComponent />
                                                            }
                                                            <motion.div
                                                                className="pm-agent-message-content"
                                                                initial={{ y: 100 }}
                                                                animate={{ y: 0 }}
                                                                style={{ whiteSpace: 'pre-wrap' }}
                                                            >
                                                                <MessageWithDbComponent
                                                                    message={message}
                                                                    isLast={false}
                                                                    projectId={projectId}
                                                                    type="pm-step"
                                                                />
                                                            </motion.div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        }
                                    }
                                    )
                                }
                            </>
                        )}
                        {messages?.map((message, index) => (
                            <div
                                ref={(el: HTMLDivElement | null) => { messageRefs.current[index] = el as HTMLDivElement; }}
                                key={message.id}
                                className={`pm-agent-message ${message.role}`}
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    minHeight: index === messages.length - 1 && lastMessageHeight ? `${lastMessageHeight}px` : 'auto'
                                }}
                            >
                                {(message.role === 'agent' || message.role === 'loading')
                                    ? <JoyAvatar size="chat" />
                                    : <UserAvatarComponent />
                                }
                                <motion.div
                                    className="pm-agent-message-content"
                                    initial={{ y: 100 }}
                                    animate={{ y: 0 }}
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    <MessageWithDbComponent
                                        message={message}
                                        isLast={(messages.length - 1) === index}
                                        projectId={projectId}
                                        type="pm-step"
                                    />
                                </motion.div>
                            </div>
                        ))}
                    </div>

                    {
                        messages?.length >= 2 && (
                            <Button
                                onClick={handleExecutePlan}
                                className="pm-agent-execute-plan-btn mx-auto my-1 mb-3"
                                fullWidth={false}
                                variant="default"
                            >
                                <Hammer size={16} />
                                Execute Plan
                            </Button>
                        )
                    }

                    {showScrollIndicator && (
                        <Button
                            onClick={handleScrollToBottom}
                            className="pm-agent-scroll-btn"
                            variant="secondaryForeground"
                            >
                        <MoveDownIcon size={16} color="white" />
                        </Button>       
                    )}

                    <CustomInputComponent
                        onSend={handleSendMessage}
                        value={chatInput}
                        onInputChange={setChatInput}
                        isNew={false}
                        loading={isChatInProgress}
                        selectedFiles={[]}
                        onFileRemove={() => { }}
                        projectId={projectId}
                        imageFile={imageFile}
                        onImageFileChange={setImageFile}
                        hideVoiceMode={true}
                        hideFileAttachment={true}
                        agentId="pm-agent" // PM agent identifier
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PmAgentDialogComponent;
